/**
 * Runtime decoding and customer-safe selection helpers for the additive
 * Watches signal catalog returned by the authenticated contracts endpoint.
 */
import { z } from "zod";
import { getReviewedSignalPresenter } from "./watchSignalReleaseRegistry";

export const WATCH_SIGNAL_CATALOG_SCHEMA_VERSION = 1 as const;

const SIGNAL_FAMILIES = [
  "PRICE",
  "VOLATILITY",
  "TREND",
  "MOMENTUM",
  "VOLUME",
  "LIQUIDITY",
  "OPTIONS_CHAIN",
  "POSITION_RISK",
  "EVENT",
] as const;

const SIGNAL_JOBS = ["POSITION", "OPTIONS_ENTRY", "MARKET"] as const;
const SUBJECT_SCOPES = [
  "UNDERLYING",
  "OPTION_CONTRACT",
  "EXPIRY_CHAIN",
  "POSITION",
  "POSITION_LEG",
  "ENTRY_CANDIDATE",
] as const;
const SIGNAL_OPERATORS = ["gt", "gte", "lt", "lte", "eq"] as const;
const SIGNAL_UNITS = [
  "USD_PER_SHARE",
  "PERCENT",
  "DECIMAL",
  "COUNT",
  "DAYS",
  "RATIO",
  "INDEX_POINTS",
] as const;
const PARAMETER_KINDS = [
  "SYMBOL",
  "DECIMAL",
  "INTEGER",
  "ENUM",
  "BOOLEAN",
  "INTERVAL",
  "WINDOW",
  "OPTION_CONTRACT",
] as const;
const AVAILABILITY_STATES = ["AVAILABLE", "UNAVAILABLE"] as const;

/** A stable customer-facing catalog family. */
export type WatchSignalFamily = typeof SIGNAL_FAMILIES[number];
/** The trade-stage job in which a signal may be authored. */
export type WatchSignalJob = typeof SIGNAL_JOBS[number];
/** The exact domain subject measured by a signal. */
export type WatchSignalSubjectScope = typeof SUBJECT_SCOPES[number];
/** A server-approved deterministic scalar comparison. */
export type WatchSignalOperator = typeof SIGNAL_OPERATORS[number];
/** The canonical unit carried by a threshold and observation. */
export type WatchSignalUnit = typeof SIGNAL_UNITS[number];
/** The runtime value kind accepted by a signal parameter. */
export type WatchSignalParameterKind = typeof PARAMETER_KINDS[number];
/** Whether a signal definition can currently be offered to a customer. */
export type WatchSignalAvailabilityState = typeof AVAILABILITY_STATES[number];

/** A bounded, server-owned authoring parameter descriptor. */
export interface WatchSignalParameterDescriptor {
  /** Stable parameter identifier used in preview and confirmation payloads. */
  readonly parameter_id: string;
  /** Runtime input kind; clients must not infer it from the parameter name. */
  readonly kind: WatchSignalParameterKind;
  /** Whether preview rejects an omitted value. */
  readonly required: boolean;
  /** Canonical unit, when the parameter is a measured value. */
  readonly unit?: WatchSignalUnit;
  /** Inclusive lower bound encoded as a decimal string. */
  readonly minimum?: string;
  /** Inclusive upper bound encoded as a decimal string. */
  readonly maximum?: string;
  /** Maximum decimal scale accepted by the server. */
  readonly max_decimal_places?: number;
  /** Allowlisted values for an enum parameter. */
  readonly allowed_values?: readonly string[];
  /** Server-owned default represented without coercion. */
  readonly default_value?: string | number | boolean;
}

/** Safe availability metadata; it never grants authority by itself. */
export interface WatchSignalAvailability {
  /** Current server-owned rollout availability. */
  readonly state: WatchSignalAvailabilityState;
  /** Whether ordinary customers may see and author the definition. */
  readonly customer_visible: boolean;
  /** Stable safe reason token required for unavailable definitions. */
  readonly reason_code?: string;
}

/** Plain-text copy suitable for ordinary customer surfaces. */
export interface WatchSignalCustomerCopy {
  /** Short signal name. */
  readonly name: string;
  /** Concise explanation of the condition's purpose. */
  readonly description: string;
}

/** An immutable, versioned scalar signal definition. */
export interface WatchSignalDefinition {
  /** Stable public signal identifier. */
  readonly signal_id: string;
  /** Immutable behavior version; semantic changes require a new value. */
  readonly definition_version: number;
  /** Curated family used for progressive disclosure. */
  readonly family: WatchSignalFamily;
  /** Trade-stage jobs in which this definition is eligible. */
  readonly eligible_jobs: readonly WatchSignalJob[];
  /** Exact subject scopes the definition can measure. */
  readonly subject_scopes: readonly WatchSignalSubjectScope[];
  /** Deterministic comparisons supported by the definition. */
  readonly operators: readonly WatchSignalOperator[];
  /** Canonical output and threshold unit. */
  readonly unit: WatchSignalUnit;
  /** Runtime-validated authoring parameter descriptors. */
  readonly parameters: readonly WatchSignalParameterDescriptor[];
  /** Server rollout and customer-visibility state. */
  readonly availability: WatchSignalAvailability;
  /** Plain-text customer-facing copy. */
  readonly customer: WatchSignalCustomerCopy;
}

/** The only signal catalog schema understood by this client version. */
export interface WatchSignalCatalog {
  /** Additive contract schema version. */
  readonly schema_version: typeof WATCH_SIGNAL_CATALOG_SCHEMA_VERSION;
  /** Fully validated immutable definitions. */
  readonly signals: readonly WatchSignalDefinition[];
}

/** Filters used by catalog-driven authoring surfaces. */
export interface WatchSignalSelection {
  /** Required trade-stage job. */
  readonly job: WatchSignalJob;
  /** Optional exact subject scope. */
  readonly subjectScope?: WatchSignalSubjectScope;
}

/** A curated family and the authorable definitions it contains. */
export interface CuratedWatchSignalFamily {
  /** Stable catalog family identifier. */
  readonly family: WatchSignalFamily;
  /** Plain-language family name. */
  readonly name: string;
  /** Plain-language family description. */
  readonly description: string;
  /** Visible definitions matching the requested job and scope. */
  readonly signals: readonly WatchSignalDefinition[];
}

const CURATED_FAMILY_COPY: Readonly<Record<WatchSignalFamily, {
  readonly name: string;
  readonly description: string;
}>> = {
  PRICE: { name: "Price & return", description: "Market levels and price movement." },
  TREND: { name: "Trend", description: "Direction and moving-average relationships." },
  MOMENTUM: { name: "Momentum", description: "The strength and pace of market movement." },
  VOLATILITY: { name: "Volatility", description: "Expected and realized market movement." },
  VOLUME: { name: "Volume", description: "Trading activity relative to a defined baseline." },
  LIQUIDITY: { name: "Liquidity", description: "Market depth and execution quality." },
  OPTIONS_CHAIN: { name: "Options chain", description: "Conditions across contracts and expirations." },
  POSITION_RISK: { name: "Position risk", description: "Profit, loss, time, and strike exposure for a position." },
  EVENT: { name: "Events", description: "Scheduled market and company events." },
};

const DECIMAL_PATTERN = /^-?(?:0|[1-9]\d*)(?:\.\d+)?$/;
const IDENTIFIER_PATTERN = /^[a-z][a-z0-9_.-]{0,127}$/;
const PARAMETER_ID_PATTERN = /^[a-z][a-z0-9_]{0,63}$/;
const REASON_CODE_PATTERN = /^[A-Z][A-Z0-9_]{0,63}$/;

const hasNoControlCharacters = (value: string) => Array.from(value).every((character) => {
  const codePoint = character.codePointAt(0) ?? 0;
  return codePoint >= 32 && codePoint !== 127;
});
const uniqueValues = (values: readonly string[]) => new Set(values).size === values.length;

const parameterSchema = z.object({
  parameter_id: z.string().regex(PARAMETER_ID_PATTERN),
  kind: z.enum(PARAMETER_KINDS),
  required: z.boolean(),
  unit: z.enum(SIGNAL_UNITS).optional(),
  minimum: z.string().regex(DECIMAL_PATTERN).optional(),
  maximum: z.string().regex(DECIMAL_PATTERN).optional(),
  max_decimal_places: z.number().int().min(0).max(12).optional(),
  allowed_values: z.array(z.string().trim().min(1)).min(1).refine(uniqueValues).optional(),
  default_value: z.union([z.string(), z.number(), z.boolean()]).optional(),
}).strict();

const availabilitySchema = z.object({
  state: z.enum(AVAILABILITY_STATES),
  customer_visible: z.boolean(),
  reason_code: z.string().regex(REASON_CODE_PATTERN).optional(),
}).strict().superRefine((availability, context) => {
  if (availability.state === "UNAVAILABLE" && availability.reason_code === undefined) {
    context.addIssue({
      code: "custom",
      message: "Unavailable signals require a safe reason code.",
      path: ["reason_code"],
    });
  }
});

const customerTextSchema = (maximumLength: number) => z.string()
  .trim()
  .min(1)
  .max(maximumLength)
  .refine(hasNoControlCharacters);

const signalDefinitionSchema = z.object({
  signal_id: z.string().regex(IDENTIFIER_PATTERN),
  definition_version: z.number().int().safe().positive(),
  family: z.enum(SIGNAL_FAMILIES),
  eligible_jobs: z.array(z.enum(SIGNAL_JOBS)).min(1).refine(uniqueValues),
  subject_scopes: z.array(z.enum(SUBJECT_SCOPES)).min(1).refine(uniqueValues),
  operators: z.array(z.enum(SIGNAL_OPERATORS)).min(1).refine(uniqueValues),
  unit: z.enum(SIGNAL_UNITS),
  parameters: z.array(parameterSchema).refine(
    (parameters) => uniqueValues(parameters.map((parameter) => parameter.parameter_id)),
  ),
  availability: availabilitySchema,
  customer: z.object({
    name: customerTextSchema(80),
    description: customerTextSchema(280),
  }).strict(),
}).strict();

const signalCatalogSchema = z.object({
  schema_version: z.literal(WATCH_SIGNAL_CATALOG_SCHEMA_VERSION),
  signals: z.array(signalDefinitionSchema).refine(
    (signals) => uniqueValues(signals.map(
      (signal) => `${signal.signal_id}:${signal.definition_version}`,
    )),
  ),
}).strict();

/**
 * Decode a signal catalog from an untrusted API payload.
 *
 * Missing catalogs support older servers by returning `null`. Unknown schema
 * versions and any invalid definition also return `null`, ensuring malformed
 * metadata cannot authorize a customer-facing signal.
 */
export function decodeWatchSignalCatalog(value: unknown): WatchSignalCatalog | null {
  const result = signalCatalogSchema.safeParse(value);
  return result.success ? result.data : null;
}

/** Return the exact immutable signal definition, or `null` when it is absent. */
export function findWatchSignal(
  catalog: WatchSignalCatalog | null,
  signalId: string,
  definitionVersion: number,
): WatchSignalDefinition | null {
  return catalog?.signals.find(
    (signal) => signal.signal_id === signalId
      && signal.definition_version === definitionVersion,
  ) ?? null;
}

/**
 * Return customer-visible definitions that are safe for the requested job.
 *
 * The local full-contract release registry is intentionally narrower than the
 * decoded catalog. A newly advertised or semantically mutated definition
 * remains unavailable until its complete immutable shape is reviewed.
 */
export function getAuthorableWatchSignals(
  catalog: WatchSignalCatalog | null,
  selection: WatchSignalSelection,
): readonly WatchSignalDefinition[] {
  if (!catalog) return [];
  return catalog.signals.filter((signal) => (
    getReviewedSignalPresenter(signal) !== null
    && signal.availability.state === "AVAILABLE"
    && signal.availability.customer_visible
    && signal.eligible_jobs.includes(selection.job)
    && (!selection.subjectScope || signal.subject_scopes.includes(selection.subjectScope))
  ));
}

/** Group authorable definitions under stable progressive-disclosure copy. */
export function getCuratedWatchSignalFamilies(
  catalog: WatchSignalCatalog | null,
  selection: WatchSignalSelection,
): readonly CuratedWatchSignalFamily[] {
  const signals = getAuthorableWatchSignals(catalog, selection);
  return SIGNAL_FAMILIES.flatMap((family) => {
    const familySignals = signals.filter((signal) => signal.family === family);
    if (familySignals.length === 0) return [];
    return [{ family, ...CURATED_FAMILY_COPY[family], signals: familySignals }];
  });
}
